import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRight, KeyRound, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Monogram } from "@/components/Monogram";
import { guestApi, guestToken, errMsg } from "@/lib/api";
import GuestSite from "@/guest/GuestSite";

const RequestCode = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await guestApi.post("/guest/request-code", { email });
      setDone(data.message);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return <p className="rounded-xl bg-[var(--ivory-2)] p-4 text-sm text-slate-700" data-testid="request-code-success">{done}</p>;
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="request-code-form">
      <label className="label-lux text-left">Email on the guest list</label>
      <input type="email" required className="input-lux" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="request-code-email-input" />
      <button className="btn-ink w-full" disabled={busy} data-testid="request-code-submit-btn">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Email me my code
      </button>
      <button type="button" onClick={onBack} className="text-sm text-slate-500 underline-offset-4 hover:underline" data-testid="request-code-back-btn">I have my code</button>
    </form>
  );
};

const Gate = ({ onIn }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [asking, setAsking] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await guestApi.post("/guest/login", { code });
      guestToken.set(data.token);
      onIn();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-clip bg-[var(--wine)] px-5 py-10" data-testid="guest-gate">
      <img src="/media/monogram.png" alt="" className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 opacity-[.07]" />
      <img src="/media/monogram.png" alt="" className="pointer-events-none absolute -bottom-28 -right-28 h-[28rem] w-[28rem] opacity-[.07]" />
      <div className="rise relative w-full max-w-md rounded-[28px] bg-[var(--ivory)] px-7 py-10 text-center shadow-2xl sm:px-10">
        <Monogram size={104} className="mx-auto" testId="gate-monogram" />
        <div className="eyebrow mt-5">A private celebration</div>
        <h1 className="font-serif-d mt-3 text-4xl sm:text-5xl">You're invited</h1>
        <div className="mt-8">
          {asking ? <RequestCode onBack={() => setAsking(false)} /> : (
            <form onSubmit={submit} className="space-y-4">
              <label className="label-lux text-left" htmlFor="code">Invitation code</label>
              <input id="code" autoComplete="one-time-code" maxLength={6} value={code} data-testid="gate-code-input"
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                className={`input-lux text-center font-serif-d !text-2xl uppercase tracking-[.5em] ${error ? "err" : ""}`} placeholder="••••••" />
              {error && <p className="text-sm text-red-700" data-testid="gate-error">{error}</p>}
              <button className="btn-ink w-full !py-3.5" disabled={busy || code.length !== 6} data-testid="gate-submit-btn">
                {busy ? <Loader2 className="animate-spin" size={16} /> : null} Open my invitation <ArrowRight size={16} />
              </button>
              <button type="button" onClick={() => setAsking(true)} className="inline-flex items-center gap-2 text-sm text-[var(--wine)] underline-offset-4 hover:underline" data-testid="gate-request-code-link">
                <KeyRound size={15} /> Don't have your code? Get it by email
              </button>
            </form>
          )}
        </div>
        <div className="hairline my-7" />
        <Link to="/admin" className="text-xs tracking-wide text-slate-400 hover:text-slate-600" data-testid="gate-organizer-login-link">Organizer login</Link>
      </div>
    </div>
  );
};

export default function GuestGate() {
  const { linkToken, previewToken } = useParams();
  const navigate = useNavigate();
  if (previewToken) guestToken.setPreview(previewToken);
  const [authed, setAuthed] = useState(!!guestToken.get() && !linkToken);

  useEffect(() => {
    if (previewToken) { navigate("/", { replace: true }); }
  }, [previewToken, navigate]);
  const [linkErr, setLinkErr] = useState("");
  const signOut = useCallback(() => { guestToken.clear(); setAuthed(false); }, []);

  useEffect(() => {
    if (!linkToken) { return; }
    guestApi.post("/guest/link", { link_token: linkToken })
      .then(({ data }) => { guestToken.set(data.token); setAuthed(true); navigate("/", { replace: true }); })
      .catch((e) => { setLinkErr(errMsg(e)); navigate("/", { replace: true }); });
  }, [linkToken, navigate]);

  useEffect(() => {
    if (linkErr) { toast.error(linkErr); }
  }, [linkErr]);

  if (linkToken) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-[var(--gold)]" /></div>;
  if (authed) return <GuestSite onSignOut={signOut} />;
  return <Gate onIn={() => setAuthed(true)} />;
}
